import { itemRegistry } from '../registries/ItemRegistry.js';

/**
 * ItemGenerator - Creates items from bespoke definitions
 *
 * Bespoke items have fixed stats per tier - no procedural generation.
 * Generation is a simple lookup: pick item from registry, apply tier's stat_values.
 */
export class ItemGenerator {
    // Current item generation version (v3 = bespoke items)
    static GENERATION_VERSION = 3;

    // Rarity prefixes for item names
    static RARITY_PREFIXES = {
        faded: 'Faded',
        standard: 'Standard',
        pristine: 'Pristine',
        golden: 'Golden',
        vectorized: 'Vectorized'
    };

    // Static method to compute scaled effects based on item level (for legacy items)
    // Level 1 = 1.0x, Level 10 = 1.9x (10% increase per level)
    // itemUpgradeBonus: multiplier for per-level scaling (0.0 to 0.25 from driver skill)
    static computeScaledEffects(baseEffects, level, itemUpgradeBonus = 0) {
        // Base: 10% per level, with bonus: 10% * (1 + bonus) per level
        const bonusPerLevel = 0.1 * (1 + itemUpgradeBonus);
        const scaleFactor = 1 + bonusPerLevel * (level - 1);
        return baseEffects.map(effect => {
            let scaledValue;
            if (effect.operation === 'multiply') {
                // For multipliers: scale the bonus portion
                scaledValue = 1 + (effect.value - 1) * scaleFactor;
            } else if (effect.type === 'momentumRate') {
                // MomentumRate needs 3 decimal precision
                scaledValue = parseFloat((effect.value * scaleFactor).toFixed(3));
            } else {
                // Other additive stats use 1 decimal
                scaledValue = parseFloat((effect.value * scaleFactor).toFixed(1));
            }
            return { ...effect, value: scaledValue };
        });
    }

    /**
     * Create an ItemGenerator
     * @param {Object} config - Configuration object (only rarityPrefixes used)
     * @param {Object} options - Optional settings (useRegistry always true for v3)
     */
    constructor(config = {}, options = {}) {
        this.config = config;
        // v3 always uses registry - no more baseItems mode
        this._registry = itemRegistry;

        // Use config prefixes if provided, otherwise use static defaults
        this.rarityPrefixes = config.rarityPrefixes || ItemGenerator.RARITY_PREFIXES;
    }

    /**
     * Check if the generator is properly initialized
     * @returns {boolean} True if registry has items loaded
     */
    isInitialized() {
        return this._registry && this._registry.size > 0;
    }

    /**
     * Get the registry being used
     * @returns {ItemRegistry}
     */
    getRegistry() {
        return this._registry;
    }

    /**
     * Get all base items from the registry
     * Provides backwards compatibility for code referencing baseItems
     * @returns {Array<Object>} Array of all item definitions
     */
    get baseItems() {
        return this._registry.getAll();
    }

    /**
     * Generate a unique item ID
     * @param {string} baseItemId - Base item template ID
     * @param {string} rarity - Item rarity tier
     * @returns {string} Unique item ID
     */
    generateItemId(baseItemId, rarity) {
        const timestamp = Date.now();
        const random = Math.floor(Math.random() * 10000);
        return `${baseItemId}_${rarity}_${timestamp}_${random}`;
    }

    /**
     * Build item name with rarity prefix
     * @param {string} baseName - Base item name
     * @param {string} rarity - Item rarity tier
     * @returns {string} Full item name with prefix
     */
    buildItemName(baseName, rarity) {
        const prefix = this.rarityPrefixes[rarity];
        if (!prefix || prefix === '') {
            return baseName;
        }
        return `${prefix} ${baseName}`;
    }

    /**
     * Generate a complete item from bespoke definition
     * Items have fixed stats per tier - no randomization
     * Supports both new multi-effect format and legacy single-stat format
     * @param {string} baseItemId - Base item template ID
     * @param {string} rarity - Item rarity tier (faded/standard/pristine/golden/vectorized)
     * @returns {Object|null} Generated item object, or null if not found
     */
    generateItem(baseItemId, rarity) {
        if (!this._registry.has(baseItemId)) {
            console.error(`Base item not found: ${baseItemId}`);
            return null;
        }
        const baseItem = this._registry.get(baseItemId);

        let effects;

        // New format: effects array with per-stat stat_values
        if (baseItem.effects && Array.isArray(baseItem.effects)) {
            effects = baseItem.effects.map(effectDef => {
                const statValues = baseItem.stat_values[effectDef.type];
                if (!statValues || statValues[rarity] === undefined) {
                    console.error(`No stat value for ${effectDef.type} at rarity ${rarity} on item ${baseItemId}`);
                    return null;
                }
                return {
                    type: effectDef.type,
                    value: statValues[rarity],
                    operation: effectDef.operation || 'add'
                };
            }).filter(e => e !== null);

            if (effects.length === 0) {
                console.error(`No valid effects for item ${baseItemId} at rarity ${rarity}`);
                return null;
            }
        } else {
            // Legacy format: single stat_type with tier values directly in stat_values
            const statValue = baseItem.stat_values[rarity];
            if (statValue === undefined) {
                console.error(`No stat value for rarity ${rarity} on item ${baseItemId}`);
                return null;
            }

            // Determine operation based on stat type
            // Multiplier stats use 'multiply', everything else uses 'add'
            const isMultiplier = ['acceleration', 'mileMultiplier'].includes(baseItem.stat_type);

            effects = [{
                type: baseItem.stat_type,
                value: statValue,
                operation: isMultiplier ? 'multiply' : 'add'
            }];
        }

        return {
            id: this.generateItemId(baseItemId, rarity),
            baseId: baseItemId,
            name: this.buildItemName(baseItem.name, rarity),
            description: baseItem.description,
            slot: baseItem.slot,
            slotType: baseItem.slot,  // UI expects slotType
            rarity: rarity,
            model: baseItem.model,
            effects: effects,
            generationVersion: ItemGenerator.GENERATION_VERSION,
            isLegacy: false
            // NO stars field - removed in v3
        };
    }

    /**
     * Generate a random item using weighted selection
     * @param {string} rarity - Item rarity tier
     * @param {string|null} slotType - Optional slot type filter
     * @returns {Object|null} Generated item, or null if no items available
     */
    generateRandomItem(rarity, slotType = null) {
        const baseItem = this._registry.getWeightedRandomItem(slotType);
        if (!baseItem) return null;
        return this.generateItem(baseItem.id, rarity);
    }
}
